import React, { useState, useEffect } from 'react';
import { independentPaymentRemindersService, independentItinerariesService } from '../../services/independent';
import { Bell, Calendar, DollarSign, Send, Edit2, Trash2, Clock, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { parseServerDateTime, formatTravelDateDisplay, getTimeZoneAbbr } from '../../utils/dateUtils';

const IndependentPaymentRemindersEnhanced = () => {
  const [reminders, setReminders] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    itinerary_id: '',
    payment_type: 'deposit',
    payment_amount: '',
    reminder_date: '',
    reminder_time: '',
    due_date: '',
    custom_message: '',
    notify_agent: true,
    notify_agent_sms: false,
    notify_client: true,
    notify_client_sms: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [remindersData, itinerariesData] = await Promise.all([
        independentPaymentRemindersService.getAll(),
        independentItinerariesService.getAll()
      ]);
      
      setReminders(Array.isArray(remindersData) ? remindersData : remindersData.data || []);
      setItineraries(Array.isArray(itinerariesData) ? itinerariesData : itinerariesData.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      itinerary_id: '',
      payment_type: 'deposit',
      payment_amount: '',
      reminder_date: '',
      reminder_time: '',
      due_date: '',
      custom_message: '',
      notify_agent: true,
      notify_agent_sms: false,
      notify_client: true,
      notify_client_sms: false
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const localDateTime = new Date(`${formData.reminder_date}T${formData.reminder_time}:00`);
      const reminderDateTime = localDateTime.toISOString();
      
      const submissionData = {
        ...formData,
        reminder_date: reminderDateTime
      };
      
      await independentPaymentRemindersService.create(submissionData);
      setShowCreateModal(false);
      resetForm();
      await loadData();
      alert('Payment reminder created successfully!');
    } catch (error) {
      console.error('Error creating reminder:', error);
      alert('Failed to create payment reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (reminder) => {
    setSelectedReminder(reminder);
    
    const reminderDateTime = parseServerDateTime(reminder.reminder_date) || new Date();
    const year = reminderDateTime.getFullYear();
    const month = String(reminderDateTime.getMonth() + 1).padStart(2, '0');
    const day = String(reminderDateTime.getDate()).padStart(2, '0');
    const hours = String(reminderDateTime.getHours()).padStart(2, '0');
    const minutes = String(reminderDateTime.getMinutes()).padStart(2, '0');
    
    setFormData({
      itinerary_id: reminder.itinerary_id,
      payment_type: reminder.payment_type,
      payment_amount: reminder.payment_amount,
      reminder_date: `${year}-${month}-${day}`,
      reminder_time: `${hours}:${minutes}`,
      due_date: reminder.due_date.split('T')[0],
      custom_message: reminder.custom_message || '',
      notify_agent: reminder.notify_agent,
      notify_agent_sms: reminder.notify_agent_sms,
      notify_client: reminder.notify_client,
      notify_client_sms: reminder.notify_client_sms
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const localDateTime = new Date(`${formData.reminder_date}T${formData.reminder_time}:00`);
      const reminderDateTime = localDateTime.toISOString();
      
      const submissionData = {
        ...formData,
        reminder_date: reminderDateTime
      };
      
      await independentPaymentRemindersService.update(selectedReminder.id, submissionData);
      setShowEditModal(false);
      setSelectedReminder(null);
      resetForm();
      await loadData();
      alert('Payment reminder updated successfully!');
    } catch (error) {
      console.error('Error updating reminder:', error);
      alert('Failed to update payment reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment reminder?')) return;
    
    try {
      await independentPaymentRemindersService.delete(id);
      await loadData();
      alert('Payment reminder deleted successfully!');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete payment reminder');
    }
  };

  const handleSendNow = async (id) => {
    if (!window.confirm('Send this reminder now?')) return;
    
    try {
      await independentPaymentRemindersService.sendNow(id);
      await loadData();
      alert('Payment reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send payment reminder');
    }
  };

  const stats = {
    scheduled: reminders.filter(r => r.status === 'scheduled').length,
    sent: reminders.filter(r => r.status === 'sent').length,
    overdue: reminders.filter(r => new Date(r.due_date) < new Date() && r.status !== 'sent').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Reminders</h1>
          <p className="text-gray-600 mt-1">Schedule automated payment reminders for clients</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Bell size={18} />
          <span>Create Reminder</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sent</p>
              <p className="text-2xl font-bold text-green-600">{stats.sent}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Reminders List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {reminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 mb-4">No payment reminders scheduled</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Your First Reminder
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Itinerary</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Client</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Payment Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reminder Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Due Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reminders.map((reminder) => (
                  <tr key={reminder.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{reminder.itinerary_title}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{reminder.client_name}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {reminder.payment_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      ${parseFloat(reminder.payment_amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{parseServerDateTime(reminder.reminder_date)?.toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Clock size={12} />
                        <span>{parseServerDateTime(reminder.reminder_date)?.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', timeZoneName: 'short'})}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${new Date(reminder.due_date) < new Date() ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                        {formatTravelDateDisplay(reminder.due_date)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        reminder.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                        reminder.status === 'sent' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {reminder.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        {reminder.status === 'scheduled' && (
                          <>
                            <button
                              onClick={() => handleSendNow(reminder.id)}
                              className="p-1 text-gray-600 hover:text-blue-600"
                              title="Send Now"
                            >
                              <Send size={16} />
                            </button>
                            <button
                              onClick={() => handleEdit(reminder)}
                              className="p-1 text-gray-600 hover:text-green-600"
                              title="Edit"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(reminder.id)}
                              className="p-1 text-gray-600 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                        {reminder.status === 'sent' && (
                          <span className="text-sm text-green-600 flex items-center">
                            <CheckCircle size={16} className="mr-1" />
                            Sent {parseServerDateTime(reminder.sent_at)?.toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {showCreateModal ? 'Create Payment Reminder' : 'Edit Payment Reminder'}
              </h2>
              <form onSubmit={showCreateModal ? handleCreate : handleUpdate}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary *</label>
                    <select
                      value={formData.itinerary_id}
                      onChange={(e) => setFormData({ ...formData, itinerary_id: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Itinerary</option>
                      {itineraries.map(itinerary => (
                        <option key={itinerary.id} value={itinerary.id}>
                          {itinerary.trip_name} - {itinerary.client_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type *</label>
                    <select
                      value={formData.payment_type}
                      onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="deposit">Deposit</option>
                      <option value="final_payment">Final Payment</option>
                      <option value="installment">Installment</option>
                      <option value="balance">Balance</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
                    <input
                      type="number"
                      value={formData.payment_amount}
                      onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Date *</label>
                    <input
                      type="date"
                      value={formData.reminder_date}
                      onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Time *</label>
                    <input
                      type="time"
                      value={formData.reminder_time}
                      onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Times use your device's time zone ({getTimeZoneAbbr(new Date(`${formData.reminder_date || ''}T${formData.reminder_time || '12:00'}:00`))}).
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Due Date *</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Message</label>
                    <textarea
                      value={formData.custom_message}
                      onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional custom message to include in the reminder"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notification Settings</label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.notify_client}
                        onChange={(e) => setFormData({ ...formData, notify_client: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Notify client via email</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.notify_client_sms}
                        onChange={(e) => setFormData({ ...formData, notify_client_sms: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Notify client via SMS</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent}
                        onChange={(e) => setFormData({ ...formData, notify_agent: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Notify me via email</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent_sms}
                        onChange={(e) => setFormData({ ...formData, notify_agent_sms: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700">Notify me via SMS</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setSelectedReminder(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {submitting ? 'Saving...' : showCreateModal ? 'Create Reminder' : 'Update Reminder'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndependentPaymentRemindersEnhanced;
